/**
 * meterServiceV2.ts
 *
 * Service for meter V2-related API calls, including CRUD operations and list fetching.
 */
import { api, apiUrls } from "../../api";
import { GetListResponse } from "@refinedev/core";
import { Meter } from "../../interface/meterTypes";
import { SidebarPermissionMap } from "../../store/slice/sidebarMenuSlice";
import { ApiResponse } from "../../api/api";
import { SERVICE_CONSTANTS } from "../../constant/stringConstant";
import Cookies from "js-cookie";

/**
 * meterServiceV2
 * Provides methods for meter V2-related API calls and operations.
 */
export const meterServiceV2 = {
  /**
   * getList
   * Fetches a paginated list of meters with optional query and date filters.
   */
  getList: async ({
    pagination,
    searchField,
    query,
    date,
    signal,
  }: {
    pagination: { current: number; pageSize: number };
    query?: string;
    date?: string;
    searchField?: string;
    signal?: AbortSignal;
  }): Promise<GetListResponse<Meter>> => {
    const { current = 1, pageSize = SERVICE_CONSTANTS.DEFAULT_PAGE_SIZE } = pagination ?? {};
    try {
      const response: ApiResponse<{ meterlist: Meter[]; metercount: number }> =
        await api.get(apiUrls.FetchMeterListV2, {
          params: {
            page: current,
            limit: pageSize,
            ...(query && { query }),
            ...(date && { date }),
            ...(searchField && { searchField }),
          },
          signal,
        });

      return {
        data: response.Data?.meterlist || [],
        total: response.Data?.metercount || 0,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  /**
   * create
   * Creates a new meter with the provided variables.
   */
  create: async (variables: any) => {
    try {
      const response: ApiResponse<any[]> = await api.post(
        apiUrls.CreateMeterV2,
        variables
      );
      return {
        data: response.Data,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  editMeter: async (data: { meterSerial: string; meterState: string }) => {
    try {
      const res: ApiResponse<any[]> = await api.put(apiUrls.EditMeterV2, data);
      return res.Data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  dissociateMeter: async (data: {
    reason: string;
    comment: string;
    meter_serial: string;
    meter_id: string;
  }) => {
    try {
      const response: ApiResponse<any> = await api.post(
        apiUrls.DissociateMeter,
        data
      );
      return {
        data: response.Data,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  searchMeters: async (
    query: string,
    page = 1,
    limit = SERVICE_CONSTANTS.DEFAULT_PAGE_SIZE,
    date: string
  ): Promise<GetListResponse<Meter>> => {
    try {
      const response: ApiResponse<{ meterlist: Meter[]; metercount: number }> =
        await api.get(apiUrls.SearchData, {
          params: {
            page,
            limit,
            ...(query && { query }),
            ...(date && { date }),
          },
        });

      return {
        data: response.Data?.meterlist || [],
        total: response.Data?.metercount || 0,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  getUnassignedMeter: async (): Promise<any[]> => {
    try {
      const res: ApiResponse<any[]> = await api.get(
        apiUrls.GetAllUnassignedMeters
      );
      return res.Data || [];
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  getPlants: async (Ids: string[], version: string = 'V2'): Promise<any[]> => {
    let body: { nocIds: string[]; countryIds: string[]; stateIds: string[]; version: string } = { nocIds: [], countryIds: [], stateIds: [], version };
    const accessLevel = JSON.parse(Cookies.get("accessLevels")?.toLocaleLowerCase() || "[]");
    if (accessLevel === "country") {
      body.countryIds = Ids;
    } else if (accessLevel === "state") {
      body.stateIds = Ids;
    } else if (accessLevel === "noc") {
      body.nocIds = Ids;
    }
    try {
      const res: ApiResponse<any[]> = await api.post(
        apiUrls.FetchPlantDetails,
        body
      );
      return res.Data || [];
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  getPolesByPlantId: async (plantId: number | string): Promise<any[]> => {
    try {
      const res: ApiResponse<any[]> = await api.post(apiUrls.FetchPoleDetails, {
        plantId: plantId,
      });
      return res.Data || [];
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  getPlantServersByPlantId: async (plantId: number): Promise<any[]> => {
    try {
      const res: ApiResponse<any[]> = await api.post(
        apiUrls.FetchServerDetails,
        {
          plantId: plantId,
        }
      );
      return res.Data || [];
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  checkPermissions: async (permissionNames: string[]): Promise<any> => {
    try {
      const payload = {
        requestType: SERVICE_CONSTANTS.CHECK_PERMISSION,
        permissionName: permissionNames,
      };
      const response: ApiResponse<any[]> = await api.post(
        apiUrls.HandlePermission,
        payload
      );
      return response.Data || {};
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },

  getSidebarPermissions: async (signal?: AbortSignal): Promise<{ permissions: SidebarPermissionMap; accessIds: string[],accessLevels:any }> => {
    try {
      const response: ApiResponse<{
        permissions: { systemName: string; allowed: boolean }[];
        accessIds?: string[];
        accessLevel?: string;
      }> = await api.post(apiUrls.HandlePermission, { permissionName: [] }, { signal });
      const permissionMap: SidebarPermissionMap = {};
      (response.Data?.permissions || []).forEach(
        (p: { systemName: string; allowed: boolean }) => {
          permissionMap[p.systemName] = p.allowed;
        }
      );

      return {
        permissions: permissionMap,
        accessIds: response.Data?.accessIds || [],
        accessLevels: response.Data?.accessLevel || ""
      };
    } catch (error:any) { 
      throw error;
    }
  },
};
